import { useState, useRef } from 'react';
import { X, Upload, Image } from 'lucide-react';
import { apiUrl } from '../utils/api';

interface CoverImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  campaignId: string;
  currentImageUrl?: string;
}

export function CoverImageModal({ isOpen, onClose, onSuccess, campaignId, currentImageUrl }: CoverImageModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (selected.size > 10 * 1024 * 1024) {
      setError('La imagen no puede pesar más de 10MB');
      return;
    }

    setFile(selected);
    setError('');
    // Create preview
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result as string);
    reader.readAsDataURL(selected);
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const fd = new FormData();
      fd.append('coverImage', file);

      const response = await fetch(apiUrl(`/api/campaigns/${campaignId}`), {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });

      const result = await response.json();

      if (result.success) {
        onSuccess();
        onClose();
        setFile(null);
        setPreview(null);
      } else {
        setError(result.error?.message || 'Error al subir imagen');
      }
    } catch {
      setError('Error de conexión');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2 mb-4">
          <Image className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">Portada de campaña</h3>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-lg text-sm">{error}</div>
        )}

        {/* Preview */}
        <div className="mb-4">
          {preview ? (
            <img src={preview} alt="Preview" className="w-full h-48 object-cover rounded-lg border" />
          ) : currentImageUrl ? (
            <img src={currentImageUrl} alt="Portada actual" className="w-full h-48 object-cover rounded-lg border" />
          ) : (
            <div className="w-full h-48 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
              <div className="text-center">
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Sin imagen de portada</p>
              </div>
            </div>
          )}
        </div>

        {/* File input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileChange}
          className="hidden"
        />

        <div className="flex gap-3">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex-1 inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Upload className="w-4 h-4 mr-2" />
            {file ? 'Cambiar imagen' : 'Seleccionar imagen'}
          </button>

          {file && (
            <button
              onClick={handleUpload}
              disabled={isLoading}
              className="flex-1 inline-flex items-center justify-center px-4 py-2.5 bg-indigo-600 rounded-lg text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? 'Subiendo...' : 'Guardar portada'}
            </button>
          )}
        </div>

        <p className="text-xs text-gray-400 mt-3 text-center">JPG, PNG o WebP. Máximo 10MB.</p>
      </div>
    </div>
  );
}
